import React, { useState, useContext, useEffect, useRef } from 'react';
import { changedOverrideKeys, effectiveParams, MAX_SIMS, MIN_SIMS, newSim, SimParams, SimSetup, simRunName, withOverride } from './multiSim';
import { useTranslation } from 'react-i18next';
import NumberField from '../../common/NumberField';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  Box,
  Divider,
  Switch,
  Slider,
  IconButton,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Tabs,
  Tab,
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { AppContext, Layer, Shape, Point, Service, Line } from '../../../context/context';

import { mapFleetToApiFleetSettings, getValidPoints } from '../utils/fleetMappers';
import type { Fleet } from '../components/FleetMenu';
import type { OperatingPeriod, SimulationConfig, SimulationRunSummary } from '../../../types/simulation';

import { mapSimulationToProblemConfig } from '../../../services/simulation/mappers';
import { simulationService, SimulationService } from '../../../services/simulation/simulationService';
import { mapGeneratedRidesToApi } from '../../../services/simulation/mappers';
import { validate as validateUuid } from 'uuid';
import { logEvent, trackAnalyticsEvent } from '../../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../../services/eventTaxonomy';
import TimeSettingsPresetSelector from './TimeSettingsPresetSelector';
import { detectCurrentPreset } from './presets/timeSettingsPresets';

import Tooltip from '@mui/material/Tooltip';
import SimulationProgressModal from './SimulationProgressModal';
import CreateServiceFromSimulationModal from '../components/CreateServiceFromSimulationModal';
import { useSimulation, QueuedRunStatus, StepStatus } from '../../../context/SimulationContext';
import { fetchSimulationMetrics, fetchSimulationActivities, fetchSimulationRidePoints, buildRidePointsFromActivities } from '../../../services/simulation/resultsApi';
import type { FrodoConfig } from '../../../types/frodo';
import { useFixedRoutes } from '../../../context/FixedRoutesContext';
import { processFrodoOptimization, generateFrodoReport } from '../../../services/frodo/frodoService';
import FrodoConfigPanel from '../../simulation/FrodoConfigPanel';
import { FeatureGate } from '../../common/FeatureGate';
import { AppError } from '../../../types/error.d';
import { LoggingService } from '../../../services/logging/loggingService';
import {
  runPreFlightDiagnostics,
  runPostFailureDiagnostics,
  augmentErrorWithDiagnosis,
} from '../../../services/simulation/simulationDiagnostics';
import { SeverityLevel } from '../../../services/logging/types';
import { DEFAULT_CLUSTERING_SETTINGS, DEFAULT_MATCHER_FLAGS, pashaRunSettingsFromRunBulk } from '../../../services/simulation/clusteringDefaults';
import { lineAddressPlaceholders } from '../../../utils/rides/addressPlaceholders';
import { combineDateWithTime, getDefaultRequestTime, parseTimeInput } from '../../../utils/dateTimeUtils';
import { DEFAULT_SIMULATION_DATE } from '../../../config/constants';
import { getCanonicalTripLines } from '../components/auto-fleet-generator/tripAnalysis';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

// Helper function to categorize vehicles by service type
function categorizeVehicles(vehicles: any[]): {
  total: number;
  onDemand: number;
  mixed: number;
  fixedRoute: number;
} {
  let onDemand = 0;
  let mixed = 0;
  let fixedRoute = 0;

  vehicles.forEach(vehicle => {
    const periods = vehicle.operatingHours || [];
    const serviceTypes = new Set<string>();
    
    periods.forEach((period: OperatingPeriod) => {
      const serviceType = period.serviceType;
      if (serviceType === 'fixed') {
        serviceTypes.add('fixed');
      } else if (serviceType === 'on-demand') {
        serviceTypes.add('on-demand');
      } else if (serviceType === 'mixed') {
        serviceTypes.add('mixed');
      } else if (!serviceType) {
        // Legacy vehicles without serviceType - assume on-demand
        serviceTypes.add('on-demand');
      }
    });

    if (serviceTypes.has('mixed')) {
      mixed++;
    } else if (serviceTypes.has('on-demand') && serviceTypes.has('fixed')) {
      mixed++;
    } else if (serviceTypes.has('on-demand')) {
      onDemand++;
    } else if (serviceTypes.has('fixed')) {
      fixedRoute++;
    } else {
      // No service type specified - assume on-demand for backward compatibility
      onDemand++;
    }
  });

  return {
    total: vehicles.length,
    onDemand,
    mixed,
    fixedRoute,
  };
}

// Types for simulation config
export type SimulationType = 'excel' | 'bulk' | 'p2p' | 'p2h';
export type RidesSource = 'layer' | 'upload' | 'generate';

interface SimulationModalProps {
  open: boolean;
  onClose: () => void;
  fleets: Fleet[];
  modalLayers: { id: string; name: string; type: 'point' | 'line' }[];
  onRunSimulation: (config: any) => void;
  setSimulationProgress?: (progress: any) => void;
  onOpenDrawer?: () => void;
  recentRideLayerId?: string;
  recentFleetId?: string;
  /** Opens the Fleet Operations (timeline) modal for the given fleet. Closes this modal first. */
  onEditFleet?: (fleetId: string) => void;
}

const steps = [
  'Rides and Fleet',
  'Time & Scenario Settings',
];

const REQUESTED_MATCHER_FLAGS: string[] = [
  'activity-time-setter-v2',
  'matcher-perf-parallel-cost-matrix',
  'matcher-clamp-start-and-end-activities',
  'matcher-decrease-ruin-count',
  'skipRoundTripCheck',
  'faster-jsprit-convergence',
  'enable-legacy-path-descriptions',
  'matcher-prevent-shuttle-round-trips',
  'matcher-prevent-round-trips',
  'matcher-desired-time-pp-vttm-intra-chain-bounds',
  'matcher-desired-time-post-processor',
  'matcher-desired-time-pp-v2',
  'matcher-enable-solve-progress-reporting',
];

const MATCHER_FLAG_ALIASES: Record<string, string> = {
  'faster-jsprit-convergence': 'matcher-faster-jsprit-convergence',
};

const ADDITIONAL_MATCHER_FLAG_OPTIONS: string[] = Array.from(
  new Set(
    REQUESTED_MATCHER_FLAGS
      .map((flag) => MATCHER_FLAG_ALIASES[flag] ?? flag)
      .filter((flag) => !DEFAULT_MATCHER_FLAGS.includes(flag))
  )
);

// The modal unmounts when the user leaves the map, so the last-used tuning
// parameters are kept in localStorage and restored on the next open.
const SIMULATION_PARAMS_STORAGE_KEY = 'atlas_simulation_params';

type SavedSimulationParams = Partial<{
  selectedServiceId: string;
  timeSettings: any;
  rideTypes: RideType[];
  passengerTypes: PassengerType[];
  numberOfPassengers: PaxGroup[];
  timeWindowSlope: number;
  timeWindowIntercept: number;
  timeWindowFixedWindow: number;
  selectedTimePreset: string;
  isManualTimeMode: boolean;
  travelTimeMultiplier: number;
  useVariableMultipliers: boolean;
  hourlyMultipliers: number[];
  vttmLockTtmAtOne: boolean;
  maxTripTimeMinutes: number;
  clusteringEnabled: boolean;
  multiSim: boolean;
  sims: SimSetup[];
}>;

/** Everything one run needs that can differ between the sims of a multi-simulation run. */
interface SimulationRun {
  name: string;
  layerId: string;
  fleetId: string;
  params: SimParams;
}

type RunOutcome = { status: 'finished' | 'failed'; error?: string };

const NOT_STARTED_STEPS: StepStatus[] = ['notStarted', 'notStarted', 'notStarted', 'notStarted', 'notStarted', 'notStarted'];

const loadSimulationParams = (): SavedSimulationParams => {
  try {
    const parsed = JSON.parse(localStorage.getItem(SIMULATION_PARAMS_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const defaultTimeSettings = {
  slope: 2.0,
  intercept: 1200,
  window: 1200,
  multiplier: 1,
  onceOffProcessingTime: 15,
  processingTimes: [
    { seatType: 'standard', processingTimeSeconds: 30 },
    { seatType: 'wheelchair', processingTimeSeconds: 300 },
  ],
};

interface RideType {
  type: 'departure_fixed' | 'arrival_fixed';
  probability: number;
}

// 1. Add interfaces for passenger types and number of passengers
interface PassengerType {
  passenger_type: string;
  processing_time: number;
  probability: number;
}
interface PaxGroup {
  number_of_passengers: number;
  probability: number;
}
type ProcessingTime = { seatType: string; processingTimeSeconds: number };

// Step tracker component
const stepTitles = [
  'Upload Rides',
  'Create Fleet',
  'Calculate Time Windows',
  'Create Problem Config',
  'Create Scenario',
  'Run Simulation'
];

const statusIcons = {
  notStarted: '⚪', // gray
  inProgress: (
    <span className="animate-pulse text-blue-500">●</span>
  ), // throbbing blue
  success: '✅', // green
  failed: '❌', // red
};

function SimulationStepTracker({ stepStatuses }: { stepStatuses: ('notStarted' | 'inProgress' | 'success' | 'failed')[] }) {
  const { t } = useTranslation('mapCore');
  const trackerStepKeys = ['uploadRides', 'createFleet', 'calculateTimeWindows', 'createProblemConfig', 'createScenario', 'runSimulation'];
  return (
    <div className="fixed top-24 right-8 w-64 bg-white shadow-lg rounded-lg p-4 z-50 border border-gray-200">
      <h3 className="text-lg font-bold mb-2">{t('simulationModal.trackerTitle')}</h3>
      <ul className="space-y-2">
        {trackerStepKeys.map((key, idx) => (
          <li key={key} className="flex items-center space-x-3">
            <span className={
              stepStatuses[idx] === 'success' ? 'text-green-500' :
              stepStatuses[idx] === 'failed' ? 'text-red-500' :
              stepStatuses[idx] === 'inProgress' ? 'text-blue-500' :
              'text-gray-400'
            }>
              {statusIcons[stepStatuses[idx]]}
            </span>
            <span className="text-sm">{t(`simulationModal.trackerSteps.${key}`)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const SimulationModal: React.FC<SimulationModalProps> = ({
  open,
  onClose,
  fleets,
  modalLayers,
  onRunSimulation,
  setSimulationProgress,
  onOpenDrawer,
  recentRideLayerId,
  recentFleetId,
  onEditFleet,
}) => {
  const { t } = useTranslation('mapCore');
  const loggingService = LoggingService.getInstance();
  // Stepper
  const [activeStep, setActiveStep] = useState(0);
  const [saved] = useState(loadSimulationParams);
  // Skip the service effect's first overwrite so restored values win over the service's defaults
  const restoringServiceRef = useRef(Boolean(saved.selectedServiceId));

  // Main state
  const [simType, setSimType] = useState<SimulationType>('excel');
  const [ridesSource, setRidesSource] = useState<RidesSource>('layer');
  const [selectedLayer, setSelectedLayer] = useState<string>(recentRideLayerId || '');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [timeSettings, setTimeSettings] = useState<any>(saved.timeSettings ?? defaultTimeSettings);
  const [scenarioName, setScenarioName] = useState('');
  const [fleetId, setFleetId] = useState<string>(recentFleetId || '');
  const [rideTypes, setRideTypes] = useState<RideType[]>(saved.rideTypes ?? [
    { type: 'departure_fixed', probability: 0.4 },
    { type: 'arrival_fixed', probability: 0.6 },
  ]);
  const [passengerTypes, setPassengerTypes] = useState<PassengerType[]>(saved.passengerTypes ?? [
    { passenger_type: 'standard', processing_time: 30, probability: 0.98 },
    { passenger_type: 'wheelchair', processing_time: 90, probability: 0.02 },
  ]);
  const [numberOfPassengers, setNumberOfPassengers] = useState<PaxGroup[]>(saved.numberOfPassengers ?? [
    { number_of_passengers: 1, probability: 1.0 },
    { number_of_passengers: 2, probability: 0.0 },
  ]);

  // Derived layer options
  const pointLayers = modalLayers.filter(l => l.type === 'point');
  const lineLayers = modalLayers.filter(l => l.type === 'line');

  // 1. Add state for time window settings in SimulationModal
  const [timeWindowSlope, setTimeWindowSlope] = useState<number>(saved.timeWindowSlope ?? 1.7);
  const [timeWindowIntercept, setTimeWindowIntercept] = useState<number>(saved.timeWindowIntercept ?? 600);
  const [timeWindowFixedWindow, setTimeWindowFixedWindow] = useState<number>(saved.timeWindowFixedWindow ?? 900);

  // Time settings preset state
  const [selectedTimePreset, setSelectedTimePreset] = useState<string>(saved.selectedTimePreset ?? 'ride-hailing');
  const [isManualTimeMode, setIsManualTimeMode] = useState<boolean>(saved.isManualTimeMode ?? false);
  const [timeSettingsAccordionExpanded, setTimeSettingsAccordionExpanded] = useState<boolean>(false);

  // Travel time multiplier state
  const [travelTimeMultiplier, setTravelTimeMultiplier] = useState<number>(saved.travelTimeMultiplier ?? 1.1);

  // Variable travel time multiplier state
  const [useVariableMultipliers, setUseVariableMultipliers] = useState<boolean>(saved.useVariableMultipliers ?? false);
  const [hourlyMultipliers, setHourlyMultipliers] = useState<number[]>(saved.hourlyMultipliers ?? [
    // Default values for 24 hours based on typical traffic patterns
    1.17, 1.12, 1.11, 1.07, 1.12, 1.24, 1.3, 1.3, 1.31, 1.36, 1.38, 1.29,
    1.23, 1.3, 1.36, 1.48, 1.56, 1.43, 1.48, 1.3, 1.35, 1.31, 1.3, 1.22
  ]);
  const [variableMultipliersExpanded, setVariableMultipliersExpanded] = useState<boolean>(false);
  /** When VTTM is enabled, lock base TTM at 1 by default; user can unlock to set a custom base TTM */
  const [vttmLockTtmAtOne, setVttmLockTtmAtOne] = useState<boolean>(saved.vttmLockTtmAtOne ?? true);

  // Service settings state
  const [selectedServiceId, setSelectedServiceId] = useState<string>(saved.selectedServiceId ?? '');
  const [createServiceModalOpen, setCreateServiceModalOpen] = useState<boolean>(false);

  // FRODO configuration state
  const [frodoConfig, setFrodoConfig] = useState<FrodoConfig>({
    enabled: false,
    selectedRouteIds: [],
    maxDistanceToStop: 500, // Default 500 meters
    excludeWheelchairUsers: true,
    maxWaitTimeMinutes: 10,
    allowTransfers: false,
  });
  const [frodoOffloadedRides, setFrodoOffloadedRides] = useState<any[]>([]);

  // Clustering state
  const [clusteringEnabled, setClusteringEnabled] = useState<boolean>(saved.clusteringEnabled ?? false);
  const [iterationCount, setIterationCount] = useState<number>(200);
  const [selectedMatcherFlags, setSelectedMatcherFlags] = useState<string[]>([...DEFAULT_MATCHER_FLAGS]);

  // Multi-simulation: each sim has its own rides and fleet and overrides some base parameters
  const [multiSim, setMultiSim] = useState<boolean>(Boolean(saved.multiSim) && (saved.sims?.length ?? 0) >= MIN_SIMS);
  const [sims, setSims] = useState<SimSetup[]>(saved.sims ?? []);
  /** Settings step tab: null is Base, otherwise a sim key */
  const [settingsTab, setSettingsTab] = useState<string | null>(null);

  // Get fixed routes context for FRODO
  const { routes: fixedRoutes, stops: fixedRouteStops } = useFixedRoutes();

  // Additional state variables
  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [maxTripTimeMinutes, setMaxTripTimeMinutes] = useState<number>(saved.maxTripTimeMinutes ?? 60);
  const [comingSoonModalOpen, setComingSoonModalOpen] = useState(false);

  // Simulation progress and results state
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [simulationResults, setSimulationResults] = useState<any>(null);
  const [progressUpdate, setProgressUpdate] = useState<any>(null);
  const [isSimulationRunning, setIsSimulationRunning] = useState(false);

  // Step tracker state
  const [stepStatuses, setStepStatuses] = useState<('notStarted' | 'inProgress' | 'success' | 'failed')[]>([
    'notStarted', 'notStarted', 'notStarted', 'notStarted', 'notStarted', 'notStarted'
  ]);

  // Get global from context
  const context = useContext(AppContext);
  if (!context) throw new Error('SimulationModal must be used within AppProvider');
  const { global, userData } = context;
  const userDetails = userData?.userDetails || { userId: '', email: '', fullName: '' };
  const services = global?.services || [];

  // Derived ride layers (for now, fallback to all layers)
  const rideLayers = modalLayers; // TODO: filter for ride layers if type is available

  // Use SimulationContext for global simulation info/status
  const {
    simulationName,
    setSimulationName,
    simulationId,
    setSimulationId,
    numberOfVehicles,
    setNumberOfVehicles,
    numberOfRides,
    setNumberOfRides,
    timeSettingsPreset,
    setTimeSettingsPreset,
    stepStatuses: contextStepStatuses,
    setStepStatuses: setContextStepStatuses,
    status,
    setStatus,
    setSimulationError,
    setSimulationHistory,
    runQueue,
    setRunQueue,
    isRunCancelled,
  } = useSimulation();

  // Reset to first step when modal opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setIterationCount(200);
      setSelectedMatcherFlags([...DEFAULT_MATCHER_FLAGS]);
    }
  }, [open]);

  const toggleMatcherFlag = (flag: string, checked: boolean) => {
    setSelectedMatcherFlags(prev => {
      if (checked) {
        return prev.includes(flag) ? prev : [...prev, flag];
      }
      return prev.filter(item => item !== flag);
    });
  };

  const handleAdditionalMatcherFlagsChange = (flags: string[]) => {
    setSelectedMatcherFlags((prev) => {
      const withoutAdditionalFlags = prev.filter((flag) => !ADDITIONAL_MATCHER_FLAG_OPTIONS.includes(flag));
      return [...withoutAdditionalFlags, ...flags];
    });
  };
  const selectedAdditionalMatcherFlags = selectedMatcherFlags.filter((flag) => ADDITIONAL_MATCHER_FLAG_OPTIONS.includes(flag));

  useEffect(() => {
    const params: SavedSimulationParams = {
      selectedServiceId, timeSettings, rideTypes, passengerTypes, numberOfPassengers,
      timeWindowSlope, timeWindowIntercept, timeWindowFixedWindow, selectedTimePreset,
      isManualTimeMode, travelTimeMultiplier, useVariableMultipliers, hourlyMultipliers,
      vttmLockTtmAtOne, maxTripTimeMinutes, clusteringEnabled, multiSim, sims,
    };
    try {
      localStorage.setItem(SIMULATION_PARAMS_STORAGE_KEY, JSON.stringify(params));
    } catch {
      // Storage full or blocked: the form still works, it just won't remember values
    }
  }, [
    selectedServiceId, timeSettings, rideTypes, passengerTypes, numberOfPassengers,
    timeWindowSlope, timeWindowIntercept, timeWindowFixedWindow, selectedTimePreset,
    isManualTimeMode, travelTimeMultiplier, useVariableMultipliers, hourlyMultipliers,
    vttmLockTtmAtOne, maxTripTimeMinutes, clusteringEnabled, multiSim, sims,
  ]);

  // Set default service when modal opens and services are available
  useEffect(() => {
    if (open && services.length > 0) {
      // Default to the first service if no service is selected or selected service no longer exists
      // This ensures users don't need to select a service on first run
      if (!selectedServiceId || !services.find(s => s.id === selectedServiceId)) {
        setSelectedServiceId(services[0].id);
      }
    }
  }, [open, services, selectedServiceId]);

  // Populate fields when service is selected
  useEffect(() => {
    if (selectedServiceId && services.length > 0) {
      const selectedService = services.find(s => s.id === selectedServiceId);
      if (selectedService && restoringServiceRef.current && selectedServiceId === saved.selectedServiceId) {
        restoringServiceRef.current = false;
        return;
      }
      if (selectedService) {
        // Map service settings to simulation modal fields
        setTimeWindowSlope(selectedService.timeWindowSettings.slope);
        setTimeWindowIntercept(selectedService.timeWindowSettings.intercept);
        setTimeWindowFixedWindow(selectedService.timeWindowSettings.fixedWindow);
        setMaxTripTimeMinutes(selectedService.maxTripTimeMinutes || 60);
        setTravelTimeMultiplier(selectedService.travelTimeMultiplier);
        setUseVariableMultipliers(selectedService.useVariableMultipliers);
        if (selectedService.useVariableMultipliers) {
          setVttmLockTtmAtOne(true);
        }
        if (selectedService.hourlyMultipliers) {
          setHourlyMultipliers([...selectedService.hourlyMultipliers]);
        }
        
        // Auto-expand Time Window Settings accordion when service is selected
        setTimeSettingsAccordionExpanded(true);
        
        // Map processing times
        if (selectedService.processingTimes && selectedService.processingTimes.length > 0) {
          const mappedProcessingTimes = selectedService.processingTimes.map(pt => ({
            seatType: pt.seatType,
            processingTimeSeconds: pt.processingTimeSeconds,
          }));
          setTimeSettings((prev: any) => ({
            ...prev,
            processingTimes: mappedProcessingTimes,
            onceOffProcessingTime: selectedService.onceOffProcessingTime || prev.onceOffProcessingTime,
          }));
          
          // Map to passengerTypes
          const mappedPassengerTypes = selectedService.processingTimes.map(pt => ({
            passenger_type: pt.seatType,
            processing_time: pt.processingTimeSeconds,
            probability: pt.seatType === 'standard' ? 0.98 : 0.02, // Default probabilities
          }));
          setPassengerTypes(mappedPassengerTypes);
        }
        
        // Map maxCompanions to numberOfPassengers
        if (selectedService.maxCompanions > 0) {
          const maxPax = selectedService.maxCompanions;
          const paxGroups: PaxGroup[] = [];
          for (let i = 1; i <= maxPax; i++) {
            paxGroups.push({
              number_of_passengers: i,
              probability: i === 1 ? 1.0 : 0.0, // Default: all single passengers
            });
          }
          setNumberOfPassengers(paxGroups);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServiceId, services]);

  // Validation function for simulation config
  const validateSimulationConfig = () => {
    const errors: string[] = [];
    
    // Only require service selection if services are available but none is selected
    // If no services exist, allow running with current form values
    if (services.length > 0 && !selectedServiceId) {
      const errorMessage = t('simulationModal.errors.serviceRequired');
      errors.push(errorMessage);
      loggingService.warn(errorMessage, { component: 'SimulationModal', field: 'selectedServiceId' });
    }
    if (!scenarioName?.trim()) {
      const errorMessage = t('simulationModal.errors.scenarioNameRequired');
      errors.push(errorMessage);
      loggingService.warn(errorMessage, { component: 'SimulationModal', field: 'scenarioName' });
    }
    // One check per run, so a multi-sim error names the sim and covers its overrides
    runsToStart().forEach(({ label, layerId, fleetId: runFleetId, params }) => {
      const runErrors = [
        !layerId && t('simulationModal.errors.selectRideLayer'),
        !runFleetId && t('simulationModal.errors.selectFleet'),
        params.timeWindowSlope <= 0 && t('simulationModal.errors.slopePositive'),
        params.timeWindowIntercept < 0 && t('simulationModal.errors.interceptNonNegative'),
      ].filter((e): e is string => Boolean(e));
      runErrors.forEach(error => {
        const errorMessage = label ? t('simulationModal.multiSim.simError', { suffix: label, error }) : error;
        errors.push(errorMessage);
        loggingService.warn(errorMessage, { component: 'SimulationModal', layerId, fleetId: runFleetId, params });
      });
    });
    if (!suffixesValid) {
      const errorMessage = t('simulationModal.multiSim.suffixesUnique');
      errors.push(errorMessage);
      loggingService.warn(errorMessage, { component: 'SimulationModal', suffixes: sims.map(sim => sim.suffix) });
    }

    // FRODO validation
    if (frodoConfig.enabled) {
      if (frodoConfig.selectedRouteIds.length === 0) {
        const errorMessage = t('simulationModal.errors.frodoNoRoutes');
        errors.push(errorMessage);
        loggingService.warn(errorMessage, { component: 'SimulationModal', frodoConfig: frodoConfig });
      }
      if (frodoConfig.maxDistanceToStop <= 0) {
        const errorMessage = t('simulationModal.errors.frodoDistancePositive');
        errors.push(errorMessage);
        loggingService.warn(errorMessage, { component: 'SimulationModal', frodoConfig: frodoConfig });
      }
      if (frodoConfig.maxWaitTimeMinutes <= 0) {
        const errorMessage = t('simulationModal.errors.frodoWaitPositive');
        errors.push(errorMessage);
        loggingService.warn(errorMessage, { component: 'SimulationModal', frodoConfig: frodoConfig });
      }
      if (fixedRoutes.length === 0) {
        const errorMessage = t('simulationModal.errors.frodoNoRoutesAvailable');
        errors.push(errorMessage);
        loggingService.warn(errorMessage, { component: 'SimulationModal', frodoConfig: frodoConfig });
      }
    }
    
    return errors;
  };
  
  // Check if FRODO config is valid (for button disable state)
  const isFrodoConfigValid = () => {
    if (!frodoConfig.enabled) return true; // FRODO disabled is always valid
    return (
      frodoConfig.selectedRouteIds.length > 0 &&
      frodoConfig.maxDistanceToStop > 0 &&
      frodoConfig.maxWaitTimeMinutes > 0 &&
      fixedRoutes.length > 0
    );
  };

  // Runs one simulation end to end and records it in history. Never throws.
  const runOne = async (run: SimulationRun): Promise<RunOutcome> => {
    const simulationStartMs = Date.now();
    const scenarioName = run.name;
    // Name the run before any early exit, so a failure is never shown under the previous sim's name
    setSimulationName(scenarioName || t('simulationModal.unnamedSimulation'));
    setContextStepStatuses([...NOT_STARTED_STEPS]);
    const failEarly = (errorMessage: string): RunOutcome => {
      alert(errorMessage); // Keep alert for immediate user feedback
      setIsSimulationRunning(false);
      setProgressModalOpen(false);
      setStatus('failed');
      return { status: 'failed', error: errorMessage };
    };
    const { timeWindowSlope, timeWindowIntercept, timeWindowFixedWindow, travelTimeMultiplier,
      maxTripTimeMinutes, iterationCount, clusteringEnabled } = run.params;

    let transformedRides: any[] = [];
    let transformedFleet: any = { vehicles: [] };
    let timeWindowSettings: any = null;
    let simulationConfigForService: SimulationConfig | null = null;

    try {
      setIsSimulationRunning(true);
      setProgressModalOpen(true);
      setSimulationError(null);

      // Get ride data from selected layer
      const selectedLayerObj = modalLayers.find(l => l.id === run.layerId);
      if (!selectedLayerObj || !context?.global) {
        const errorMessage = t('simulationModal.errors.noRideLayerOrContext');
        loggingService.error(errorMessage, { component: 'SimulationModal', selectedLayer: run.layerId, globalContext: !!context?.global });
        return failEarly(errorMessage);
      }

      const layer = context.global.mapLayers.find(l => l.layerId === Number(selectedLayerObj.id));
      if (!layer || !Array.isArray(layer.shapes)) {
        const errorMessage = t('simulationModal.errors.noRidesInLayer');
        loggingService.error(errorMessage, { component: 'SimulationModal', selectedLayer: run.layerId, layer });
        return failEarly(errorMessage);
      }

      const rideLayerLines = uniqueSimulationTripLines(getCanonicalTripLines(layer));
      if (rideLayerLines.length === 0) {
        const errorMessage = t('simulationModal.errors.noRideLines');
        loggingService.warn(errorMessage, { component: 'SimulationModal', selectedLayer: run.layerId, layerShapes: layer.shapes.length });
        return failEarly(errorMessage);
      }

      // Validate passenger count fields before transformation
      const allNullPassengers = rideLayerLines.every(
        (l: any) =>
          (l.standard_seat === null || l.standard_seat === undefined || l.standard_seat === '') &&
          (l.numPassengers === null || l.numPassengers === undefined || l.numPassengers === '') &&
          (l.wheelchair === null || l.wheelchair === undefined || l.wheelchair === '')
      );
      const hasNonNumericPassengers = rideLayerLines.some((l: any) => {
        const std = l.standard_seat ?? l.numPassengers;
        const wc = l.wheelchair;
        const stdInvalid = std !== null && std !== undefined && std !== '' && isNaN(Number(std));
        const wcInvalid = wc !== null && wc !== undefined && wc !== '' && isNaN(Number(wc));
        return stdInvalid || wcInvalid;
      });

      if (allNullPassengers || hasNonNumericPassengers) {
        const what = t('simulationModal.errors.invalidPassengerWhat');
        const why = allNullPassengers
          ? t('simulationModal.errors.invalidPassengerWhyAllNull')
          : t('simulationModal.errors.invalidPassengerWhyNonNumeric');
        const howToFix = t('simulationModal.errors.invalidPassengerHowToFix');
        loggingService.warn(what, { component: 'SimulationModal', why, allNullPassengers, hasNonNumericPassengers });
        setSimulationError(new AppError({ what, why, howToFix, category: 'Validation', details: { allNullPassengers, hasNonNumericPassengers } }));
        setStatus('failed');
        setIsSimulationRunning(false);
        setProgressModalOpen(false);
        emitSimulationRunFailAnalytics({
          errorCategory: 'validation',
          errorCode: 'invalid_passenger_counts',
          ridesCount: rideLayerLines.length,
          fleetSize: 0,
          durationMs: Date.now() - simulationStartMs,
        });
        return { status: 'failed', error: what };
      }

      // Transform data
      const rides = rideLayerLines.map((line: any, index: number) => mapLineToRideSchema(line, index + 1));
      
      // Apply FRODO preprocessing if enabled
      let ridesForSimulation = rides;
      let offloadedRides: any[] = [];
      
      if (frodoConfig.enabled && frodoConfig.selectedRouteIds.length > 0 && fixedRoutes.length > 0) {
        try {
          // Get selected routes
          const selectedRoutes = fixedRoutes.filter(route => 
            frodoConfig.selectedRouteIds.includes(route.id)
          );
          
          if (selectedRoutes.length > 0 && fixedRouteStops.length > 0) {
            // Process FRODO optimization
            const frodoResult = processFrodoOptimization(
              rides,
              selectedRoutes,
              fixedRouteStops,
              frodoConfig
            );
            
            ridesForSimulation = frodoResult.remainingRides;
            offloadedRides = frodoResult.offloadedRides;
            setFrodoOffloadedRides(offloadedRides);
            
            loggingService.info(`FRODO: Offloaded ${offloadedRides.length} rides, ${ridesForSimulation.length} remaining for simulation`, {
              scenarioName,
              offloadedCount: offloadedRides.length,
              remainingCount: ridesForSimulation.length
            });
            
            // Warn if no rides were offloaded but FRODO is enabled
            if (offloadedRides.length === 0 && rides.length > 0) {
              loggingService.warn('FRODO: Enabled but no rides were offloaded. Check route configuration and distance/time constraints.', { scenarioName, frodoConfig });
            }
          } else {
            loggingService.warn('FRODO: Enabled but no valid routes or stops found. Using all rides for simulation.', { scenarioName, frodoConfig });
            setFrodoOffloadedRides([]);
          }
        } catch (error) {
          loggingService.error('FRODO preprocessing failed, using all rides.', {
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            scenarioName,
            frodoConfig
          });
          setSimulationError(new AppError({
            what: t('simulationModal.errors.frodoPreprocessingFailed'),
            why: error instanceof Error ? error.message : t('simulationModal.errors.frodoPreprocessingUnknown'),
            howToFix: t('simulationModal.errors.frodoPreprocessingFix'),
            category: 'BusinessLogic',
            details: error
          }));
          // Fail-safe: use all rides if FRODO fails
          ridesForSimulation = rides;
          setFrodoOffloadedRides([]);
        }
      } else {
        setFrodoOffloadedRides([]);
      }
      
      transformedRides = mapGeneratedRidesToApi(ridesForSimulation);
      const selectedFleet = fleets.find(f => f.id === run.fleetId);
      if (!selectedFleet) {
        const errorMessage = t('simulationModal.errors.fleetNotFound');
        loggingService.error(errorMessage, { component: 'SimulationModal', fleetId: run.fleetId });
        return failEarly(errorMessage);
      }
      transformedFleet = mapFleetToApiFleetSettings(selectedFleet, getValidPoints(context?.global?.mapLayers?.flatMap(layer => layer.shapes.filter((s: any) => s.type === 'point')) || []));

      // Pre-flight diagnostics: check for common failure patterns before starting
      const { errors: preFlightErrors } = runPreFlightDiagnostics(transformedRides, transformedFleet);
      if (preFlightErrors.length > 0) {
        const first = preFlightErrors[0];
        setSimulationError(new AppError({
          what: first.what,
          why: first.why,
          howToFix: first.howToFix,
          category: 'Validation',
          details: { diagnosis: first, allErrors: preFlightErrors }
        }));
        setStatus('failed');
        setIsSimulationRunning(false);
        setProgressModalOpen(false);
        emitSimulationRunFailAnalytics({
          errorCategory: 'validation',
          errorCode: 'preflight_validation_failed',
          ridesCount: transformedRides.length,
          fleetSize: transformedFleet?.vehicles?.length || 0,
          durationMs: Date.now() - simulationStartMs,
        });
        return { status: 'failed', error: first.what };
      }

      emitSimulationRunStartAnalytics({
        ridesCount: transformedRides.length,
        fleetSize: transformedFleet?.vehicles?.length || 0,
        frodoEnabled: frodoConfig.enabled,
      });

      const problemPayload = {
        id: window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c: string) =>
              (
                parseInt(c, 10) ^ (window.crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> (parseInt(c, 10) / 4))
              ).toString(16)
            ),
        configuration: {
          config_name: `${scenarioName || 'simulation'}-config`,
          iteration_count: iterationCount,
          travelTimeMultiplier: (useVariableMultipliers && vttmLockTtmAtOne) ? 1 : travelTimeMultiplier,
          detailed_path_required: true,
          ...(useVariableMultipliers && {
            variable_travel_time_multipliers: hourlyMultipliers
          })
        }
      };

      const timeWindowSettingsForService = buildTimeWindowSettings(
        timeWindowSlope,
        timeWindowIntercept,
        timeWindowFixedWindow,
        timeSettings.onceOffProcessingTime,
        timeSettings.processingTimes,
        scenarioName,
        maxTripTimeMinutes,
        ''
      );

      simulationConfigForService = {
        scenarioName,
        rides: transformedRides,
        timeWindowSettings: timeWindowSettingsForService,
        fleet: transformedFleet,
        run_bulk_simulation: {
          matcher_flags: selectedMatcherFlags,
          clustering: { ...DEFAULT_CLUSTERING_SETTINGS, enabled: clusteringEnabled },
        },
        problem_config: problemPayload,
      } as SimulationConfig;

      timeWindowSettings = timeWindowSettingsForService;

      loggingService.info('Simulation config payload built', {
        component: 'SimulationModal',
        serviceId: selectedServiceId,
        rides: `[${transformedRides.length} rides]`,
        fleet: `[${transformedFleet.vehicles.length} vehicle types]`,
      });

      // Update global simulation context for drawer/indicators
      const totalVehicles = transformedFleet?.vehicles?.reduce(
        (sum: number, vehicle: any) => sum + (vehicle.number_of_vehicles || 0),
        0
      ) || 0;
      setNumberOfRides(transformedRides.length);
      setNumberOfVehicles(totalVehicles);
      setTimeSettingsPreset(selectedTimePreset || 'Custom');
      setSimulationId('');
      setStatus('running');

      const completedResults = await simulationService.runSimulation(
        simulationConfigForService,
        (progress) => {
          loggingService.info('Simulation task progress', { scenarioName, progress });
          const status = progress.status?.toLowerCase();
          if (setSimulationProgress) {
            setSimulationProgress({
              steps: stepTitles,
              currentStep: 5,
              isRunning: status === 'running',
              result: status === 'completed' ? progress : undefined,
              error: status === 'failed' ? progress?.message || 'Simulation failed' : undefined,
            });
          }
        },
        setContextStepStatuses
      );
      setSimulationResults(completedResults);
      setIsSimulationRunning(false);
      setProgressModalOpen(false);
      if (setSimulationProgress) setSimulationProgress({ steps: stepTitles, currentStep: 6, isRunning: false, result: completedResults });
      const resolvedRunId = completedResults?.results_id || '';
      setSimulationId(resolvedRunId);

      const [metricsResult, activitiesResult, ridePointsResult] = await Promise.allSettled([
        fetchSimulationMetrics(resolvedRunId),
        fetchSimulationActivities(resolvedRunId),
        fetchSimulationRidePoints(resolvedRunId),
      ]);

      if (metricsResult.status === 'rejected') {
        loggingService.warn('Unable to fetch simulation metrics', { scenarioName, runId: resolvedRunId, error: metricsResult.reason });
      }
      if (activitiesResult.status === 'rejected') {
        loggingService.warn('Unable to fetch simulation activities', { scenarioName, runId: resolvedRunId, error: activitiesResult.reason });
      }
      if (ridePointsResult.status === 'rejected') {
        loggingService.warn('Unable to fetch simulation ride points', { scenarioName, runId: resolvedRunId, error: ridePointsResult.reason });
      }

      const metricsRow: Partial<SimulationRunSummary> | null = metricsResult.status === 'fulfilled' ? metricsResult.value : null;
      const activities: any[] | undefined = activitiesResult.status === 'fulfilled' ? activitiesResult.value : undefined;
      const ridePointsFromApi = ridePointsResult.status === 'fulfilled' ? ridePointsResult.value : null;

      const derivedRidePoints =
        (ridePointsFromApi && ridePointsFromApi.length
          ? ridePointsFromApi
          : buildRidePointsFromActivities(activities || [])) || transformedRides;

      // Generate FRODO report if FRODO was enabled and rides were offloaded
      let frodoReport = undefined;
      if (frodoConfig.enabled && offloadedRides.length > 0) {
        try {
          // Create stops map for report generation
          const stopsMap = new Map<string, any>();
          fixedRouteStops.forEach((stop) => {
            stopsMap.set(stop.id, stop);
          });
          frodoReport = generateFrodoReport(offloadedRides, rides.length, stopsMap);
        } catch (error) {
          loggingService.error('Failed to generate FRODO report:', { scenarioName, error: error instanceof Error ? error.message : String(error), stack: error instanceof Error ? error.stack : undefined });
        }
      }

      const runSummary: SimulationRunSummary = {
        run_id: resolvedRunId || `sim-${Date.now()}`,
        scenario_name: metricsRow?.scenario_name || scenarioName || t('simulationModal.unnamedSimulation'),
        run_time: metricsRow?.run_time || new Date().toISOString(),
        success_ratio:
          metricsRow?.success_ratio ??
          (completedResults?.summary.total_rides
            ? completedResults.summary.assigned_rides / completedResults.summary.total_rides
            : undefined),
        no_rides: metricsRow?.no_rides ?? completedResults?.summary.total_rides ?? transformedRides.length,
        no_vehicles: metricsRow?.no_vehicles ?? totalVehicles,
        allocated_vehicle_shifts: metricsRow?.allocated_vehicle_shifts ?? totalVehicles,
        ride_points: derivedRidePoints,
        activities,
        metrics_fetched: Boolean(metricsRow),
        frodoEnabled: frodoConfig.enabled,
        frodoReport,
        frodoOffloadedRides: offloadedRides.length > 0 ? offloadedRides : undefined,
        ...metricsRow,
      };
      setSimulationHistory(prev => [runSummary, ...prev.filter(run => run.run_id !== runSummary.run_id)]);
      emitSimulationRunCompleteAnalytics({
        runId: runSummary.run_id,
        ridesCount: transformedRides.length,
        fleetSize: transformedFleet.vehicles.length,
        successRatio: runSummary.success_ratio,
        durationMs: Date.now() - simulationStartMs,
      });
      setStatus('finished');
      return { status: 'finished' };
    } catch (err) {
      let appError = SimulationService.normalizeError(err);
      // Post-failure diagnostics: try to explain the failure based on what we sent
      const diagnosis = runPostFailureDiagnostics(transformedRides, transformedFleet, appError.message);
      if (diagnosis) {
        appError = augmentErrorWithDiagnosis(appError, diagnosis);
        loggingService.info('Simulation failure diagnosed', { diagnosis: diagnosis.id, scenarioName });
      }
      // Store simulation inputs in error.details for Download JSON (not displayed in UI)
      const simulationInputs = {
        scenarioName: scenarioName ?? null,
        rides: transformedRides?.slice(0, 5) ?? [],
        ridesTotalCount: transformedRides?.length ?? 0,
        fleet: transformedFleet ?? null,
        timeWindowSettings: timeWindowSettings ?? null,
        problemConfig: simulationConfigForService?.problem_config ?? null,
        runBulkSimulation: simulationConfigForService?.run_bulk_simulation ?? null,
        rideBatchId: null,
        fleetId: null,
        timestamp: new Date().toISOString(),
      };
      const errorWithInputs = new AppError({
        ...appError.structuredError,
        details: { ...appError.structuredError.details, simulationInputs },
      });
      loggingService.error('Simulation failed unexpectedly in handleSimulationStart', {
        error: appError.message,
        stack: appError.stack,
        scenarioName,
        details: err
      });
      setIsSimulationRunning(false);
      setProgressModalOpen(false);
      setSimulationError(errorWithInputs);
      const errorMessage = appError.structuredError.what + (appError.structuredError.why ? `: ${appError.structuredError.why}` : '');
      setContextStepStatuses([ 'failed', 'failed', 'failed', 'failed', 'failed', 'failed' ]);
      if (setSimulationProgress) setSimulationProgress({ steps: stepTitles, currentStep: 0, isRunning: false, error: errorMessage });
      emitSimulationRunFailAnalytics({
        errorCategory: appError.structuredError.category || 'unknown',
        errorCode: appError.name || 'simulation_failed',
        ridesCount: transformedRides.length,
        fleetSize: transformedFleet?.vehicles?.length || 0,
        durationMs: Date.now() - simulationStartMs,
      });
      setSimulationId('');
      setStatus('failed');
      return { status: 'failed', error: errorMessage };
    }
  };

  const baseParams: SimParams = {
    timeWindowSlope, timeWindowIntercept, timeWindowFixedWindow, travelTimeMultiplier,
    maxTripTimeMinutes, iterationCount, clusteringEnabled,
  };

  /** The runs the Run button starts: one in single mode, one per sim card otherwise. */
  function runsToStart(): (Omit<SimulationRun, 'name'> & { label: string; key: string })[] {
    return multiSim
      ? sims.map(sim => ({ key: sim.key, label: sim.suffix, layerId: sim.rideLayerId, fleetId: sim.fleetId, params: effectiveParams(baseParams, sim.overrides) }))
      : [{ key: 'single', label: '', layerId: selectedLayer, fleetId, params: baseParams }];
  }
  const suffixesValid = !multiSim || (
    sims.every(sim => sim.suffix.trim()) && new Set(sims.map(sim => sim.suffix.trim())).size === sims.length
  );
  // A second start would share the context's single status with the queue and orphan its loop
  const runInProgress = status === 'running' || runQueue.some(r => r.status === 'queued' || r.status === 'running');

  const handleSimulationStart = async () => {
    if (runInProgress) return;
    const validationErrors = validateSimulationConfig();
    if (validationErrors.length > 0) {
      loggingService.warn('Simulation configuration validation failed', { errors: validationErrors });
      // The errors are already logged by validateSimulationConfig, so we just show a general alert.
      alert(`${t('simulationModal.errors.fixHeader')}\n${validationErrors.join('\n')}`);
      return;
    }
    // Open drawer and close modal when simulation starts
    if (onOpenDrawer) {
      onOpenDrawer();
    }
    onClose();

    if (!multiSim) {
      setRunQueue([]);
      const [{ key, label, ...run }] = runsToStart();
      await runOne({ ...run, name: scenarioName.trim() });
      return;
    }

    // ponytail: one after another; the backend's appetite for parallel runs per user is unknown
    const queued = runsToStart().map(run => ({ ...run, name: simRunName(scenarioName, run.label) }));
    setRunQueue(queued.map(({ key, name }) => ({ key, name, status: 'queued' })));
    for (const { key, label, ...run } of queued) {
      if (isRunCancelled(key)) continue;
      setRunStatus(key, 'running');
      const outcome = await runOne(run);
      setRunStatus(key, outcome.status, outcome.error);
    }
  };

  // Rides and vehicles behind the step 1 selections; a run needs at least one of each.
  // Trip lines are counted once per distinct layer, since every sim card shares the same layers.
  const picks = multiSim
    ? sims.map(sim => ({ suffix: sim.suffix, layerId: sim.rideLayerId, fleetId: sim.fleetId }))
    : [{ suffix: '', layerId: selectedLayer, fleetId }];
  const tripCounts = new Map<string, number>();
  picks.forEach(({ layerId }) => {
    if (tripCounts.has(layerId)) return;
    const layerObj = modalLayers.find(l => l.id === layerId);
    const mapLayer = layerObj ? global?.mapLayers.find(l => l.layerId === Number(layerObj.id)) : undefined;
    tripCounts.set(layerId, mapLayer && Array.isArray(mapLayer.shapes)
      ? uniqueSimulationTripLines(getCanonicalTripLines(mapLayer)).length
      : 0);
  });
  const vehicleCountFor = (id: string): number => fleets.find((f: any) => f.id === id)?.vehicles?.length ?? 0;
  const canLeaveRidesAndFleet = picks.length > 0 && picks.every(p => (tripCounts.get(p.layerId) ?? 0) > 0 && vehicleCountFor(p.fleetId) > 0);
  // Only explain once something is picked; an untouched form just keeps Next disabled
  const ridesAndFleetErrors = picks.flatMap(({ suffix, layerId, fleetId: id }) => {
    const error = layerId && !tripCounts.get(layerId) ? t('simulationModal.errors.noRides')
      : id && vehicleCountFor(id) === 0 ? t('simulationModal.errors.noVehicles')
      : null;
    if (!error) return [];
    return [suffix ? t('simulationModal.multiSim.simError', { suffix, error }) : error];
  });
  const setRunStatus = (key: string, status: QueuedRunStatus, error?: string) =>
    setRunQueue(queue => queue.map(r => (r.key === key ? { ...r, status, error } : r)));

  const toggleMultiSim = (enabled: boolean) => {
    if (enabled) {
      const a = newSim([], { rideLayerId: selectedLayer, fleetId });
      setSims([a, newSim([a], a)]);
    } else if (sims[0]) {
      setSelectedLayer(sims[0].rideLayerId);
      setFleetId(sims[0].fleetId);
    }
    setSettingsTab(null);
    setMultiSim(enabled);
  };
  const updateSim = (key: string, change: Partial<SimSetup>) =>
    setSims(prev => prev.map(sim => (sim.key === key ? { ...sim, ...change } : sim)));
  const removeSim = (key: string) => {
    setSims(prev => prev.filter(sim => sim.key !== key));
    if (settingsTab === key) setSettingsTab(null);
  };

  // Handlers
  const handleNext = () => {
    logEvent({
      category: 'Fleet Management',
      action: 'SimulationStepNext',
      label: steps[activeStep],
      step_number: activeStep + 1,
      total_steps: steps.length
    });
    setActiveStep(s => Math.min(s + 1, steps.length - 1));
  };
  
  const handleBack = () => {
    logEvent({
      category: 'Fleet Management',
      action: 'SimulationStepBack',
      label: steps[activeStep],
      step_number: activeStep + 1,
      total_steps: steps.length
    });
    setActiveStep(s => Math.max(s - 1, 0));
  };
  
  const handleReset = () => {
    logEvent({
      category: 'Fleet Management',
      action: 'SimulationReset',
      label: 'Simulation Modal Reset'
    });
    setActiveStep(0);
    setSimType('excel');
    setRidesSource('layer');
    setSelectedLayer('');
    setUploadedFile(null);
    setTimeSettings(defaultTimeSettings);
    setScenarioName('');
    setFleetId('');
    setRideTypes([
      { type: 'departure_fixed', probability: 0.4 },
      { type: 'arrival_fixed', probability: 0.6 },
    ]);
    setPassengerTypes([
      { passenger_type: 'standard', processing_time: 30, probability: 0.98 },
      { passenger_type: 'wheelchair', processing_time: 90, probability: 0.02 },
    ]);
    setNumberOfPassengers([
      { number_of_passengers: 1, probability: 1.0 },
      { number_of_passengers: 2, probability: 0.0 },
    ]);
    setTimeWindowSlope(1.7);
    setTimeWindowIntercept(600);
    setTimeWindowFixedWindow(900);
    setSelectedTimePreset('ride-hailing');
    setIsManualTimeMode(false);
    setTimeSettingsAccordionExpanded(false);
    setTravelTimeMultiplier(1.1);
    setUseVariableMultipliers(false);
    setHourlyMultipliers([
      1.17, 1.12, 1.11, 1.07, 1.12, 1.24, 1.3, 1.3, 1.31, 1.36, 1.38, 1.29,
      1.23, 1.3, 1.36, 1.48, 1.56, 1.43, 1.48, 1.3, 1.35, 1.31, 1.3, 1.22
    ]);
    setVariableMultipliersExpanded(false);
    setFrodoConfig({
      enabled: false,
      selectedRouteIds: [],
      maxDistanceToStop: 500,
      excludeWheelchairUsers: true,
      maxWaitTimeMinutes: 10,
      allowTransfers: false,
    });
    setFrodoOffloadedRides([]);
    setClusteringEnabled(false);
    setContextStepStatuses([ 'notStarted', 'notStarted', 'notStarted', 'notStarted', 'notStarted', 'notStarted' ]);
  };

  // File upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      logEvent({
        category: 'Fleet Management',
        action: 'SimulationFileUploaded',
        label: file.name,
        file_size: file.size,
        file_type: file.type
      });
      setUploadedFile(file);
    }
  };

  // Time settings preset handlers
  const handleTimePresetChange = (presetId: string, settings: {
    slope: number;
    intercept: number;
    window: number;
    onceOffProcessingTime: number;
    processingTimes: { seatType: string; processingTimeSeconds: number }[];
  }) => {
    setSelectedTimePreset(presetId);
    setIsManualTimeMode(presetId === 'manual');
    
    // Update time window settings
    setTimeWindowSlope(settings.slope);
    setTimeWindowIntercept(settings.intercept);
    setTimeWindowFixedWindow(settings.window);
    
    // Update processing times
    setTimeSettings({
      ...timeSettings,
      onceOffProcessingTime: settings.onceOffProcessingTime,
      processingTimes: settings.processingTimes
    });
    
    // Update travel time multiplier based on preset
    const presetTravelTimeMultipliers: { [key: string]: number } = {
      'fast': 1.0,
      'balanced': 1.1,
      'thorough': 1.2,
      'ride-hailing': 1.1,
      'transit': 1.2,
      'manual': travelTimeMultiplier // Keep current value for manual mode
    };
    
    if (presetTravelTimeMultipliers[presetId] !== undefined) {
      setTravelTimeMultiplier(presetTravelTimeMultipliers[presetId]);
    }
    
    // Expand accordion if manual mode is selected
    if (presetId === 'manual') {
      setTimeSettingsAccordionExpanded(true);
    }
    
    logEvent({
      category: 'Fleet Management',
      action: 'TimePresetChanged',
      label: presetId
    });
  };

  // Orange input style for all fields
  const orangeInputSx = {
    '& label.Mui-focused': {
      color: '#FFA726',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#FFA726',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        // No base color override
      },
      '&:hover fieldset': {
        borderColor: '#FB8C00',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#FFA726',
      },
    },
    '& .MuiSelect-icon': {
      // Only orange when focused (handled by MUI), so no base color override
    },
  };

  const renderRidesAndFleet = (
    layerId: string,
    onLayerChange: (id: string) => void,
    selectedFleetId: string,
    onFleetChange: (id: string) => void,
  ) => {
    const tripCount = tripCounts.get(layerId) ?? 0;
    return (
      <>
              <FormControl fullWidth margin="normal" sx={orangeInputSx}>
                <InputLabel>{t('simulationModal.fields.rideLayer')}</InputLabel>
                <Select
                  value={layerId}
                  label={t('simulationModal.fields.rideLayer')}
                  onChange={e => onLayerChange(e.target.value)}
                >
                  {rideLayers.map(l => (
                    <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {layerId && tripCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 2 }}>
                  <Icon size={20} name="car-side" style={{ color: '#FFA726', marginRight: 8 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFA726', mr: 2 }}>{t('simulationModal.fields.tripsCount', { count: tripCount })}</Typography>
                </Box>
              )}
              <FormControl fullWidth margin="normal" sx={orangeInputSx}>
                <InputLabel>{t('simulationModal.fields.fleet')}</InputLabel>
                <Select
                  value={selectedFleetId}
                  label={t('simulationModal.fields.fleet')}
                  onChange={e => onFleetChange(e.target.value)}
                >
                  {fleets.map(f => {
                    const counts = categorizeVehicles(f.vehicles || []);
                    const countText = [
                      counts.total > 0 && t('simulationModal.fields.fleetCounts.total', { count: counts.total }),
                      counts.onDemand > 0 && t('simulationModal.fields.fleetCounts.onDemand', { count: counts.onDemand }),
                      counts.mixed > 0 && t('simulationModal.fields.fleetCounts.mixed', { count: counts.mixed }),
                      counts.fixedRoute > 0 && t('simulationModal.fields.fleetCounts.fixedRoute', { count: counts.fixedRoute }),
                    ].filter(Boolean).join(', ');
                    return (
                      <MenuItem key={f.id} value={f.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <Typography>{f.name}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', ml: 2 }}>
                            {countText}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {selectedFleetId && (() => {
                const selectedFleet = fleets.find((f: any) => f.id === selectedFleetId);
                if (selectedFleet && selectedFleet.vehicles) {
                  const counts = categorizeVehicles(selectedFleet.vehicles);
                  return (
                    <Box sx={{ mt: 1, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Icon size={20} name="bus" style={{ color: '#FFA726', marginRight: 8 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFA726', mr: 2 }}>
                          {t('simulationModal.fields.totalVehicles', { count: counts.total })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 4 }}>
                        {counts.onDemand > 0 && (
                          <Typography variant="caption" sx={{ color: '#2196F3' }}>
                            {t('simulationModal.fields.onDemandCount', { count: counts.onDemand })}
                          </Typography>
                        )}
                        {counts.mixed > 0 && (
                          <Typography variant="caption" sx={{ color: '#FF9800' }}>
                            {t('simulationModal.fields.mixedCount', { count: counts.mixed })}
                          </Typography>
                        )}
                        {counts.fixedRoute > 0 && (
                          <Typography variant="caption" sx={{ color: '#4CAF50' }}>
                            {t('simulationModal.fields.fixedRouteCount', { count: counts.fixedRoute })}
                          </Typography>
                        )}
                      </Box>
                      {onEditFleet && (
                        <Box sx={{ mt: 1.5, ml: 4 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Icon name="sliders" size={20} />}
                            onClick={() => {
                              logEvent({
                                category: 'Fleet Management',
                                action: 'EditFleetFromSimulationModal',
                                label: selectedFleet.name,
                                fleet_id: selectedFleet.id,
                                vehicle_count: counts.total,
                              });
                              onClose();
                              onEditFleet(selectedFleet.id);
                            }}
                          >
                            {t('simulationModal.fields.editFleet')}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  );
                }
                return null;
              })()}
      </>
    );
  };

  // Settings step, multi-sim: Base holds the shared form; each sim tab only overrides SIM_PARAM_FIELDS
  const activeSettingsSim = sims.find(sim => sim.key === settingsTab);
  const SIM_PARAM_FIELDS: { key: keyof SimParams; label: string; integer?: boolean }[] = [
    { key: 'timeWindowSlope', label: t('simulationModal.timeWindowSettings.slope') },
    { key: 'timeWindowIntercept', label: t('simulationModal.timeWindowSettings.intercept') },
    { key: 'timeWindowFixedWindow', label: t('simulationModal.timeWindowSettings.window'), integer: true },
    { key: 'travelTimeMultiplier', label: t('simulationModal.ttm.label') },
    { key: 'maxTripTimeMinutes', label: t('simulationModal.maxTripTime.label'), integer: true },
    { key: 'iterationCount', label: t('simulationModal.timeWindowSettings.iterationCount'), integer: true },
    { key: 'clusteringEnabled', label: t('simulationModal.cluster.label') },
  ];
  const setSimOverride = <K extends keyof SimParams>(sim: SimSetup, key: K, value: SimParams[K]) =>
    updateSim(sim.key, { overrides: withOverride(baseParams, sim.overrides, key, value) });

  const renderSettingsTabs = () => (
    <Tabs
      value={settingsTab ?? 'base'}
      onChange={(_, value) => setSettingsTab(value === 'base' ? null : value)}
      variant="scrollable"
      sx={{ mb: 1, '& .Mui-selected': { color: '#FB8C00 !important' }, '& .MuiTabs-indicator': { backgroundColor: '#FFA726' } }}
    >
      <Tab value="base" label={t('simulationModal.multiSim.baseTab')} sx={{ textTransform: 'none' }} />
      {sims.map(sim => {
        const changed = changedOverrideKeys(baseParams, sim.overrides).length;
        return (
          <Tab
            key={sim.key}
            value={sim.key}
            sx={{ textTransform: 'none' }}
            label={changed
              ? t('simulationModal.multiSim.simTabChanged', { suffix: sim.suffix, count: changed })
              : t('simulationModal.multiSim.simLabel', { suffix: sim.suffix })}
          />
        );
      })}
    </Tabs>
  );

  const renderSimOverrides = (sim: SimSetup) => {
    const params = effectiveParams(baseParams, sim.overrides);
    const changed = changedOverrideKeys(baseParams, sim.overrides);
    return (
      <Box data-testid={`sim-modal.overrides.${sim.suffix}`}>
        <TextField
          label={t('simulationModal.multiSim.suffix')}
          value={sim.suffix}
          onChange={e => updateSim(sim.key, { suffix: e.target.value })}
          error={!sim.suffix.trim() || sims.some(other => other.key !== sim.key && other.suffix.trim() === sim.suffix.trim())}
          helperText={t('simulationModal.multiSim.suffixHelper', { name: simRunName(scenarioName || t('simulationModal.fields.simulationName'), sim.suffix) })}
          margin="normal"
          size="small"
          sx={orangeInputSx}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1.5 }}>
          {t('simulationModal.multiSim.overridesHint')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {SIM_PARAM_FIELDS
            // The run sends a TTM of 1 while variable multipliers lock it, so an override would be ignored
            .filter(({ key }) => !(key === 'travelTimeMultiplier' && useVariableMultipliers && vttmLockTtmAtOne))
            .map(({ key, label, integer }) => {
            const isChanged = changed.includes(key);
            return (
              <Box
                key={key}
                data-testid={`sim-modal.override.${key}`}
                data-changed={isChanged}
                sx={{ borderLeft: '3px solid', borderColor: isChanged ? '#FFA726' : 'transparent', pl: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 30 }}>
                  <Typography variant="body2" sx={{ fontWeight: isChanged ? 600 : 400 }}>{label}</Typography>
                  {isChanged && (
                    <Tooltip title={t('simulationModal.multiSim.resetToBase')} placement="top" arrow>
                      <IconButton
                        size="small"
                        aria-label={t('simulationModal.multiSim.resetFieldToBase', { field: label })}
                        onClick={() => setSimOverride(sim, key, baseParams[key])}
                        sx={{ ml: 0.5 }}
                      >
                        <Icon name="arrows-rotate" size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {key === 'clusteringEnabled' ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={params.clusteringEnabled}
                        onChange={e => setSimOverride(sim, 'clusteringEnabled', e.target.checked)}
                      />
                    }
                    label={params.clusteringEnabled ? t('simulationModal.cluster.enabled') : t('simulationModal.cluster.disabled')}
                  />
                ) : (
                  <NumberField
                    fullWidth
                    size="small"
                    value={params[key] as number}
                    onChange={v => setSimOverride(sim, key, (v ?? baseParams[key]) as SimParams[typeof key])}
                    allowEmpty={false}
                    fallback={baseParams[key] as number}
                    integer={integer}
                    inputProps={{ 'aria-label': label }}
                    sx={orangeInputSx}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderSimSummary = () => (
    <Box sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} data-testid="sim-modal.summary">
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('simulationModal.multiSim.summaryTitle')}</Typography>
      {sims.map(sim => {
        const changed = changedOverrideKeys(baseParams, sim.overrides);
        const layerName = modalLayers.find(l => l.id === sim.rideLayerId)?.name ?? '-';
        const fleetName = fleets.find((f: any) => f.id === sim.fleetId)?.name ?? '-';
        return (
          <Typography key={sim.key} variant="body2" sx={{ mb: 0.5 }}>
            <strong>{t('simulationModal.multiSim.simLabel', { suffix: sim.suffix })}</strong>
            {` · ${layerName} · ${fleetName} · `}
            {changed.length
              ? SIM_PARAM_FIELDS.filter(f => changed.includes(f.key)).map(f => f.label).join(', ')
              : t('simulationModal.multiSim.sameAsBase')}
          </Typography>
        );
      })}
    </Box>
  );

  // Step content
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <FormControlLabel
              control={<Checkbox checked={multiSim} onChange={e => toggleMultiSim(e.target.checked)} size="small" />}
              label={t('simulationModal.multiSim.toggle')}
            />
            {!multiSim && renderRidesAndFleet(selectedLayer, setSelectedLayer, fleetId, setFleetId)}
            {multiSim && sims.map((sim, idx) => (
              <Box
                key={sim.key}
                data-testid={`sim-modal.sim-card.${sim.suffix}`}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2, pt: 1, pb: 1, mt: 1.5 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('simulationModal.multiSim.simLabel', { suffix: sim.suffix })}
                  </Typography>
                  {idx >= MIN_SIMS && (
                    <IconButton
                      size="small"
                      aria-label={t('simulationModal.multiSim.remove', { suffix: sim.suffix })}
                      onClick={() => removeSim(sim.key)}
                    >
                      <Icon size={18} name="xmark" />
                    </IconButton>
                  )}
                </Box>
                {renderRidesAndFleet(
                  sim.rideLayerId,
                  id => updateSim(sim.key, { rideLayerId: id }),
                  sim.fleetId,
                  id => updateSim(sim.key, { fleetId: id }),
                )}
              </Box>
            ))}
            {multiSim && sims.length < MAX_SIMS && (
              <Button
                size="small"
                startIcon={<Icon size={18} name="plus" />}
                onClick={() => setSims(prev => [...prev, newSim(prev, prev[prev.length - 1])])}
                sx={{ mt: 1.5, textTransform: 'none', color: '#FB8C00' }}
              >
                {t('simulationModal.multiSim.add')}
              </Button>
            )}
          </>
        );
      case 1:
        return (
          <>
            {multiSim && renderSettingsTabs()}
            {multiSim && activeSettingsSim ? renderSimOverrides(activeSettingsSim) : (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label={t('simulationModal.fields.simulationName')}
                fullWidth
                margin="normal"
                required
                value={scenarioName}
                onChange={e => setScenarioName(e.target.value)}
                sx={orangeInputSx}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon size={20} name="gear" />}
                onClick={() => setCreateServiceModalOpen(true)}
                sx={{
                  mt: 2,
                  whiteSpace: 'nowrap',
                  minWidth: 'auto'
                }}
                title={t('simulationModal.fields.saveAsServiceTooltip')}
              >
                {t('simulationModal.fields.saveAsService')}
              </Button>
            </Box>

            {/* Service Settings Selector — only shown when services exist */}
            {services.length > 0 && (
              <FormControl fullWidth margin="normal" required>
                <InputLabel>{t('simulationModal.fields.serviceSettings')}</InputLabel>
                <Select
                  value={selectedServiceId}
                  onChange={(e) => setSelectedServiceId(e.target.value)}
                  label={t('simulationModal.fields.serviceSettings')}
                  sx={orangeInputSx}
                >
                  <MenuItem value="">
                    <em>{t('simulationModal.fields.selectServiceSetting')}</em>
                  </MenuItem>
                  {services.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Divider sx={{ my: 2 }} />
            
            {/* Time Settings Preset Selector */}
            <TimeSettingsPresetSelector
              selectedPreset={selectedTimePreset}
              onPresetChange={handleTimePresetChange}
            />
            
            {/* Travel Time Multiplier | Max Trip Time | FRODO Enable — compact row */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 2, mb: 2 }}>
              {/* Travel Time Multiplier */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{t('simulationModal.ttm.label')}</Typography>
                  <Tooltip title={
                    useVariableMultipliers
                      ? t('simulationModal.ttm.tooltipVttmEnabled')
                      : t('simulationModal.ttm.tooltipDefault')
                  } placement="top" arrow>
                    <Icon name="circle-info" size={20} style={{ marginLeft: 4, color: '#888' }} />
                  </Tooltip>
                  {useVariableMultipliers && (
                    <Tooltip title={vttmLockTtmAtOne ? t('simulationModal.ttm.lockTooltip') : t('simulationModal.ttm.unlockTooltip')} placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setVttmLockTtmAtOne((prev) => !prev)}
                        sx={{ ml: 0.5 }}
                        aria-label={vttmLockTtmAtOne ? t('simulationModal.ttm.unlockAria') : t('simulationModal.ttm.lockAria')}
                      >
                        {vttmLockTtmAtOne ? <Icon name="lock" size={20} /> : <Icon name="lock" size={20} />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <NumberField
                  value={useVariableMultipliers && vttmLockTtmAtOne ? 1 : travelTimeMultiplier}
                  onChange={(v) => setTravelTimeMultiplier(v ?? 1.1)}
                  allowEmpty={false}
                  fallback={1.1}
                  min={0.1}
                  max={5.0}
                  step={0.1}
                  error={!(useVariableMultipliers && vttmLockTtmAtOne) && (travelTimeMultiplier < 0.1 || travelTimeMultiplier > 5.0)}
                  helperText={
                    useVariableMultipliers && vttmLockTtmAtOne
                      ? t('simulationModal.ttm.lockedHelper')
                      : travelTimeMultiplier < 0.1 || travelTimeMultiplier > 5.0
                        ? t('simulationModal.ttm.outOfRangeHelper')
                        : t('simulationModal.ttm.defaultHelper')
                  }
                  fullWidth
                  size="small"
                  disabled={useVariableMultipliers && vttmLockTtmAtOne}
                  sx={orangeInputSx}
                />
              </Box>

              {/* Max Trip Time */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{t('simulationModal.maxTripTime.label')}</Typography>
                  <Tooltip title={t('simulationModal.maxTripTime.tooltip')} placement="top" arrow>
                    <Icon name="circle-info" size={20} style={{ marginLeft: 4, color: '#888' }} />
                  </Tooltip>
                </Box>
                <NumberField
                  value={maxTripTimeMinutes}
                  onChange={(v) => setMaxTripTimeMinutes(v ?? 0)}
                  allowEmpty={false}
                  fallback={0}
                  min={0}
                  step={15}
                  integer
                  fullWidth
                  size="small"
                  sx={orangeInputSx}
                />
              </Box>

              {/* FRODO Enable Toggle */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{t('simulationModal.frodo.label')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
                  <Switch
                    checked={frodoConfig.enabled}
                    onChange={e => setFrodoConfig({ ...frodoConfig, enabled: e.target.checked })}
                    color="primary"
                    size="small"
                    inputProps={{ 'aria-label': t('simulationModal.frodo.toggleAria') }}
                  />
                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                    {frodoConfig.enabled ? t('simulationModal.frodo.enabled') : t('simulationModal.frodo.disabled')}
                  </Typography>
                  {frodoConfig.enabled && (
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      {t('simulationModal.frodo.routesSelected', { count: frodoConfig.selectedRouteIds.length })}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Cluster Simulation Toggle */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{t('simulationModal.cluster.label')}</Typography>
                  <Tooltip title={t('simulationModal.cluster.tooltip')} placement="top" arrow>
                    <Icon name="circle-info" size={20} style={{ marginLeft: 4, color: '#888' }} />
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
                  <Switch
                    checked={clusteringEnabled}
                    onChange={e => setClusteringEnabled(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                    {clusteringEnabled ? t('simulationModal.cluster.enabled') : t('simulationModal.cluster.disabled')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {frodoConfig.enabled && (
              <Box sx={{ mt: 1.5, mb: 2 }}>
                <FrodoConfigPanel
                  config={frodoConfig}
                  onConfigChange={setFrodoConfig}
                  hideEnableToggle
                />
              </Box>
            )}
            
            
            {/* Time Window Settings Accordion (includes VTTM) */}
            <Accordion 
              expanded={timeSettingsAccordionExpanded}
              onChange={(_, expanded) => setTimeSettingsAccordionExpanded(expanded)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                <Typography variant="subtitle1">{t('simulationModal.timeWindowSettings.title')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Variable Travel Time Multipliers */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useVariableMultipliers}
                      onChange={(e) => setUseVariableMultipliers(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2">{t('simulationModal.timeWindowSettings.variableMultipliersLabel')}</Typography>
                      <Tooltip title={t('simulationModal.timeWindowSettings.variableMultipliersTooltip')} placement="top" arrow>
                        <Icon name="circle-info" size={20} style={{ marginLeft: 4, color: '#888' }} />
                      </Tooltip>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                />
                {useVariableMultipliers && (
                  <Box sx={{ mb: 2 }}>
                    {/* Preset Pattern Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setHourlyMultipliers([1.2, 1.1, 1.1, 1.0, 1.1, 1.3, 1.4, 1.5, 1.5, 1.4, 1.3, 1.2, 1.2, 1.3, 1.4, 1.5, 1.6, 1.5, 1.4, 1.3, 1.2, 1.1, 1.1, 1.1])}
                        sx={{ borderColor: '#FFA726', color: '#FFA726', backgroundColor: '#fff', fontWeight: 600, textTransform: 'none', '&:hover': { borderColor: '#FB8C00', color: '#FB8C00', backgroundColor: '#fff' } }}
                      >
                        {t('simulationModal.timeWindowSettings.presetRushHour')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setHourlyMultipliers([1.0, 1.0, 1.0, 1.0, 1.0, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.1, 1.0])}
                        sx={{ borderColor: '#FFA726', color: '#FFA726', backgroundColor: '#fff', fontWeight: 600, textTransform: 'none', '&:hover': { borderColor: '#FB8C00', color: '#FB8C00', backgroundColor: '#fff' } }}
                      >
                        {t('simulationModal.timeWindowSettings.presetOffPeak')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setHourlyMultipliers([1.17, 1.12, 1.11, 1.07, 1.12, 1.24, 1.3, 1.3, 1.31, 1.36, 1.38, 1.29, 1.23, 1.3, 1.36, 1.48, 1.56, 1.43, 1.48, 1.3, 1.35, 1.31, 1.3, 1.22])}
                        sx={{ borderColor: '#FFA726', color: '#FFA726', backgroundColor: '#fff', fontWeight: 600, textTransform: 'none', '&:hover': { borderColor: '#FB8C00', color: '#FB8C00', backgroundColor: '#fff' } }}
                      >
                        {t('simulationModal.timeWindowSettings.presetReset')}
                      </Button>
                    </Box>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(6, 1fr)' },
                      gap: 2,
                    }}>
                      {hourlyMultipliers.map((value, index) => (
                        <Box key={index}>
                          <TextField
                            type="number"
                            label={t('simulationModal.timeWindowSettings.hourLabel', { index })}
                            value={value}
                            onChange={(e) => {
                              const newValues = [...hourlyMultipliers];
                              newValues[index] = parseFloat(e.target.value) || 1.0;
                              setHourlyMultipliers(newValues);
                            }}
                            inputProps={{ min: 0.1, max: 5.0, step: 0.01 }}
                            size="small"
                            error={value < 0.1 || value > 5.0}
                            helperText={value < 0.1 || value > 5.0 ? t('simulationModal.timeWindowSettings.hourValidationHelper') : ''}
                            sx={orangeInputSx}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                <Divider sx={{ mb: 2 }} />
                {/* Slope, Intercept, Window on one line */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <span>{t('simulationModal.timeWindowSettings.slope')}</span>
                      <Tooltip title={t('simulationModal.timeWindowSettings.slopeTooltip')} placement="top" arrow>
                        <Icon name="circle-info" size={20} style={{ marginLeft: 4, color: '#888' }} />
                      </Tooltip>
                    </Box>
                    <NumberField
                      fullWidth
                      value={timeWindowSlope}
                      onChange={v => setTimeWindowSlope(v ?? 0)}
                      allowEmpty={false}
                      fallback={1}
                      step={0.1}
                      sx={orangeInputSx}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <span>{t('simulationModal.timeWindowSettings.intercept')}</span>
                      <Tooltip title={t('simulationModal.timeWindowSettings.interceptTooltip')} placement="top" arrow>
                        <Icon name="circle-info" size={20} style={{ marginLeft: 4, color: '#888' }} />
                      </Tooltip>
                    </Box>
                    <NumberField
                      fullWidth
                      value={timeWindowIntercept}
                      onChange={v => setTimeWindowIntercept(v ?? 0)}
                      allowEmpty={false}
                      fallback={0}
                      sx={orangeInputSx}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <span>{t('simulationModal.timeWindowSettings.window')}</span>
                      <Tooltip title={t('simulationModal.timeWindowSettings.windowTooltip')} placement="top" arrow>
                        <Icon name="circle-info" size={20} style={{ marginLeft: 4, color: '#888' }} />
                      </Tooltip>
                    </Box>
                    <NumberField
                      fullWidth
                      value={timeWindowFixedWindow}
                      onChange={v => setTimeWindowFixedWindow(v ?? 0)}
                      allowEmpty={false}
                      fallback={0}
                      integer
                      sx={orangeInputSx}
                    />
                  </Box>
                </Box>

                {/* Standard, Wheelchair, Once-off Processing Time on one line */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label={t('simulationModal.timeWindowSettings.standardProcessing')}
                    type="number"
                    fullWidth
                    value={timeSettings.processingTimes.find((pt: ProcessingTime) => pt.seatType === 'standard')?.processingTimeSeconds || ''}
                    onChange={e => {
                      const newPT = timeSettings.processingTimes.map((pt: ProcessingTime) =>
                        pt.seatType === 'standard'
                          ? { ...pt, processingTimeSeconds: Number(e.target.value) }
                          : pt
                      );
                      setTimeSettings({ ...timeSettings, processingTimes: newPT });
                    }}
                    sx={orangeInputSx}
                  />
                  <TextField
                    label={t('simulationModal.timeWindowSettings.wheelchairProcessing')}
                    type="number"
                    fullWidth
                    value={timeSettings.processingTimes.find((pt: ProcessingTime) => pt.seatType === 'wheelchair')?.processingTimeSeconds || ''}
                    onChange={e => {
                      const newPT = timeSettings.processingTimes.map((pt: ProcessingTime) =>
                        pt.seatType === 'wheelchair'
                          ? { ...pt, processingTimeSeconds: Number(e.target.value) }
                          : pt
                      );
                      setTimeSettings({ ...timeSettings, processingTimes: newPT });
                    }}
                    sx={orangeInputSx}
                  />
                  <TextField
                    label={t('simulationModal.timeWindowSettings.onceOffProcessing')}
                    type="number"
                    fullWidth
                    value={timeSettings.onceOffProcessingTime}
                    onChange={e => {
                      setTimeSettings({ ...timeSettings, onceOffProcessingTime: Number(e.target.value) });
                    }}
                    sx={orangeInputSx}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1 }}>
                    {t('simulationModal.timeWindowSettings.matcherConfiguration')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <TextField
                      label={t('simulationModal.timeWindowSettings.iterationCount')}
                      type="number"
                      value={iterationCount}
                      onChange={(e) => {
                        const nextValue = Number(e.target.value);
                        if (Number.isNaN(nextValue)) return;
                        const clampedValue = Math.max(100, Math.min(5000, nextValue));
                        setIterationCount(clampedValue);
                      }}
                      inputProps={{ min: 100, max: 5000, step: 1 }}
                      helperText={t('simulationModal.timeWindowSettings.iterationHelper')}
                      size="small"
                      sx={{ ...orangeInputSx, minWidth: 220 }}
                    />
                    <Box sx={{ minWidth: 320 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('simulationModal.timeWindowSettings.matcherFlags')}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 2 }}>
                        {DEFAULT_MATCHER_FLAGS.map((flag) => (
                          <FormControlLabel
                            key={flag}
                            control={(
                              <Checkbox
                                size="small"
                                checked={selectedMatcherFlags.includes(flag)}
                                onChange={(e) => toggleMatcherFlag(flag, e.target.checked)}
                              />
                            )}
                            label={<Typography variant="body2">{flag}</Typography>}
                            sx={{ mr: 0 }}
                          />
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ minWidth: 320, flex: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('simulationModal.timeWindowSettings.additionalMatcherFlags')}
                      </Typography>
                      <FormControl fullWidth size="small" sx={orangeInputSx}>
                        <InputLabel id="additional-matcher-flags-label">{t('simulationModal.timeWindowSettings.additionalFlags')}</InputLabel>
                        <Select
                          labelId="additional-matcher-flags-label"
                          multiple
                          value={selectedAdditionalMatcherFlags}
                          label={t('simulationModal.timeWindowSettings.additionalFlags')}
                          onChange={(e) => {
                            const value = e.target.value;
                            const nextFlags = typeof value === 'string' ? value.split(',') : value;
                            handleAdditionalMatcherFlagsChange(nextFlags as string[]);
                          }}
                          renderValue={(selected) => (selected as string[]).join(', ')}
                        >
                          {ADDITIONAL_MATCHER_FLAG_OPTIONS.map((flag) => (
                            <MenuItem key={flag} value={flag}>
                              <Checkbox checked={selectedAdditionalMatcherFlags.includes(flag)} size="small" />
                              <Typography variant="body2">{flag}</Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
            
          </Box>
            )}
            {multiSim && renderSimSummary()}
          </>
        );
      default:
        return null;
    }
  };

  // When modal opens, update selectedLayer and fleetId to recent values
  useEffect(() => {
    if (open) {
      setSelectedLayer(recentRideLayerId || '');
      setFleetId(recentFleetId || '');
    }
  }, [open, recentRideLayerId, recentFleetId]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('simulationModal.title')}</DialogTitle>
        <DialogContent>
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 3,
              '& .MuiStepIcon-root': {
                color: '#E0E0E0', // default step color
                '&.Mui-active': {
                  color: '#FFA726', // orange for active
                },
                '&.Mui-completed': {
                  color: '#FFA726', // orange for completed
                },
              },
              '& .MuiStepLabel-label.Mui-active': {
                color: '#FFA726',
                fontWeight: 600,
              },
              '& .MuiStepLabel-label.Mui-completed': {
                color: '#FFA726',
              },
            }}
          >
            {steps.map((label, idx) => (
              <Step key={label}>
                <StepLabel>{idx === 0 ? t('simulationModal.steps.ridesAndFleet') : t('simulationModal.steps.timeAndScenario')}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {renderStepContent(activeStep)}
          {activeStep === 0 && ridesAndFleetErrors.map((error, idx) => (
            <Typography key={idx} variant="body2" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              sx={{
                background: '#fff',
                color: '#222',
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                transition: 'color 0.2s, background 0.2s',
                '&:hover': {
                  background: '#FFF3E0',
                  color: '#FFA726',
                },
              }}
            >
              {t('simulationModal.actions.back')}
            </Button>
          )}
          {activeStep < steps.length - 1 && (
            <Button
              onClick={handleNext}
              disabled={activeStep === 0 && !canLeaveRidesAndFleet}
              variant="contained"
              sx={{
                background: '#FFA726',
                color: '#fff',
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                transition: 'background 0.2s, color 0.2s',
                '&:hover': {
                  background: '#FB8C00',
                  color: '#fff',
                },
              }}
            >
              {t('simulationModal.actions.next')}
            </Button>
          )}
          {activeStep === steps.length - 1 && (
            <FeatureGate featureName="simulations">
              <Button
                onClick={handleSimulationStart}
                variant="contained"
                disabled={!scenarioName.trim() || !canLeaveRidesAndFleet || !suffixesValid || isSimulationRunning || runInProgress || !isFrodoConfigValid()}
                sx={{
                  background: '#FFA726',
                  color: '#fff',
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none',
                  transition: 'background 0.2s, color 0.2s',
                  '&:hover': {
                    background: '#FB8C00',
                    color: '#fff',
                  },
                }}
              >
                {isSimulationRunning ? t('simulationModal.actions.running')
                  : multiSim ? t('simulationModal.actions.runSimulations', { count: sims.length })
                  : t('simulationModal.actions.runSimulation')}
              </Button>
            </FeatureGate>
          )}
          <Button
            onClick={() => { handleReset(); onClose(); }}
            sx={{
              background: '#fff',
              color: '#222',
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              transition: 'color 0.2s, background 0.2s',
              '&:hover': {
                background: '#FFF3E0',
                color: '#FFA726',
              },
            }}
          >
            {t('simulationModal.actions.cancel')}
          </Button>
        </DialogActions>
        <Dialog open={apiModalOpen} onClose={() => setApiModalOpen(false)}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
            <Icon size={64} d={ICON_FALLBACKS.Construction} style={{ color: 'orange', marginBottom: 16 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>{t('simulationModal.comingSoon.title')}</Typography>
            <Typography>{t('simulationModal.comingSoon.body')}</Typography>
            <Button onClick={() => setApiModalOpen(false)} sx={{ mt: 3 }} variant="contained">{t('simulationModal.actions.close')}</Button>
          </Box>
        </Dialog>
        <SimulationProgressModal
          open={progressModalOpen}
          onComplete={(results) => {
            setSimulationResults(results);
            setProgressModalOpen(false);
            // TODO: Show results or navigate to results view
            console.log('Simulation completed:', results);
          }}
          onCancel={() => {
            setProgressModalOpen(false);
            setIsSimulationRunning(false);
          }}
          progressUpdate={progressUpdate}
        />
        <CreateServiceFromSimulationModal
          open={createServiceModalOpen}
          onClose={() => setCreateServiceModalOpen(false)}
          simulationConfig={{
            scenarioName,
            timeWindowSlope,
            timeWindowIntercept,
            timeWindowFixedWindow,
            maxTripTimeMinutes,
            travelTimeMultiplier: (useVariableMultipliers && vttmLockTtmAtOne) ? 1 : travelTimeMultiplier,
            useVariableMultipliers,
            hourlyMultipliers: useVariableMultipliers ? hourlyMultipliers : undefined,
            processingTimes: timeSettings.processingTimes,
            onceOffProcessingTime: timeSettings.onceOffProcessingTime,
            passengerTypes,
            numberOfPassengers,
          }}
          onServiceCreated={(serviceId) => {
            setSelectedServiceId(serviceId);
            setCreateServiceModalOpen(false);
          }}
        />
      </Dialog>
    </>
  );
};

// Mapping function for TimeWindowSettings
function buildTimeWindowSettings(
  timeWindowSlope: number,
  timeWindowIntercept: number,
  timeWindowFixedWindow: number,
  onceOffProcessingTime: number,
  processingTimes: { seatType: string; processingTimeSeconds: number }[],
  scenarioName: string,
  maxTripTimeMinutes: number,
  rideBatchId: string
): {
  time_model_type: 'slope_and_intercept';
  settings: any;
  ride_settings: any;
} {
  return {
    time_model_type: 'slope_and_intercept',
    settings: {
      slope: timeWindowSlope,
      intercept: timeWindowIntercept,
      window: timeWindowFixedWindow,
      max_time_minutes: maxTripTimeMinutes,
      once_off_processing_time_seconds: onceOffProcessingTime,
      processing_time: (processingTimes || []).map((pt: any) => ({
        seat_type: pt.seatType,
        processing_time_seconds: pt.processingTimeSeconds,
      })),
    },
    ride_settings: {
      rides_name: scenarioName || undefined,
      batch_id: rideBatchId
    }
  };
}

/** Trip / external reference from layer line (human-readable or numeric; not limited to UUID). */
function readTripReferenceFromLine(line: Record<string, unknown>): string | undefined {
  const candidates = [
    line.ride_id,
    line.rideId,
    line.external_reference_id,
    line.externalReferenceId,
  ];
  for (const c of candidates) {
    if (c == null) continue;
    if (typeof c === 'string') {
      const t = c.trim();
      if (t.length > 0) return t;
    }
    if (typeof c === 'number' && Number.isFinite(c)) return String(c);
  }
  return undefined;
}

function readUserReferenceFromLine(line: Record<string, unknown>): string | undefined {
  const candidates = [line.user_id, line.userId, line.external_user_reference_id, line.externalUserReferenceId];
  for (const c of candidates) {
    if (c == null) continue;
    if (typeof c === 'string') {
      const t = c.trim();
      if (t.length > 0) return t;
    }
    if (typeof c === 'number' && Number.isFinite(c)) return String(c);
  }
  return undefined;
}

function uniqueSimulationTripLines(lines: Line[]): Line[] {
  const seen = new Set<string>();
  const unique: Line[] = [];
  for (const line of lines) {
    const tripRef = readTripReferenceFromLine(line as unknown as Record<string, unknown>);
    const reqTime = normalizeRideRequestTime((line as any).request_time ?? line.time ?? '');
    const from = line.coordinates?.[0];
    const to = line.coordinates?.[line.coordinates.length - 1];
    const coordKey = Array.isArray(from) && Array.isArray(to)
      ? `${from[0]},${from[1]}->${to[0]},${to[1]}`
      : String(line.id ?? '');
    const key = tripRef ? `trip:${tripRef}` : `shape:${coordKey}|${reqTime}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(line);
  }
  return unique;
}

function normalizeRideRequestTime(value: unknown): string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      if (trimmed.includes('T')) {
        return trimmed.replace('Z', '');
      }
      try {
        const hhmm = parseTimeInput(trimmed);
        return combineDateWithTime(DEFAULT_SIMULATION_DATE, hhmm);
      } catch {
        // fall through to default
      }
    }
  }
  return getDefaultRequestTime();
}

// Helper to map a line/trip to RideSchema
function mapLineToRideSchema(line: any, index: number): any {
  // Coordinates: [[from_lat, from_lng], [to_lat, to_lng]]
  const coords = Array.isArray(line.coordinates) ? line.coordinates : [];
  const from = coords[0] || [null, null];
  const to = coords[1] || [null, null];
  // Passengers array: try to use standard_seat, wheelchair, numPassengers
  const passengers: any[] = [];
  if (line.standard_seat || line.numPassengers) {
    passengers.push({ type: 'standard', number_of_passengers: Number(line.standard_seat ?? line.numPassengers ?? 1) });
  }
  if (line.wheelchair) {
    passengers.push({ type: 'wheelchair', number_of_passengers: Number(line.wheelchair) });
  }
  if (passengers.length === 0) {
    passengers.push({ type: 'standard', number_of_passengers: 1 }); // fallback
  }
  const tripRef = readTripReferenceFromLine(line as Record<string, unknown>);
  const userRef = readUserReferenceFromLine(line as Record<string, unknown>);
  // Map point ids are UUIDs from the editor; trip ids may be any stable string (e.g. Excel import).
  const fromPointId = validateUuid(line.originPointId) ? line.originPointId : undefined;
  const toPointId = validateUuid(line.destPointId) ? line.destPointId : undefined;
  const addresses = lineAddressPlaceholders(line, index);
  const ride: any = {
    ...(tripRef
      ? {
          external_reference_id: tripRef,
          ride_id: tripRef,
        }
      : {}),
    ...(userRef
      ? {
          external_user_reference_id: userRef,
          user_id: userRef,
        }
      : {}),
    from_latitude: from[0],
    from_longitude: from[1],
    from_address: addresses.from_address,
    to_latitude: to[0],
    to_longitude: to[1],
    to_address: addresses.to_address,
    request_type: line.requestType || line.request_type || 'departure_fixed',
    passengers,
    request_time: normalizeRideRequestTime(line.request_time || line.time),
    created_at: line.created_at || null,
    batch_id: line.batch_id || null,
    priority: line.priority || null,
  };
  if (fromPointId) ride.from_point_id = fromPointId;
  if (toPointId) ride.to_point_id = toPointId;
  return ride;
}

export default SimulationModal; 

export function buildSimulationRunStartPayload(args: {
  ridesCount: number;
  fleetSize: number;
  frodoEnabled: boolean;
}) {
  return {
    rides_count: args.ridesCount,
    fleet_size: args.fleetSize,
    frodo_enabled: args.frodoEnabled,
    timestamp: new Date().toISOString(),
  };
}

export function emitSimulationRunStartAnalytics(args: {
  ridesCount: number;
  fleetSize: number;
  frodoEnabled: boolean;
}) {
  trackAnalyticsEvent(
    ANALYTICS_EVENT_NAMES.simulation_run_start,
    buildSimulationRunStartPayload(args)
  );
}

export function buildSimulationRunCompletePayload(args: {
  runId: string;
  ridesCount: number;
  fleetSize: number;
  successRatio?: number;
  durationMs: number;
}) {
  return {
    run_id: args.runId,
    rides_count: args.ridesCount,
    fleet_size: args.fleetSize,
    success_ratio: args.successRatio,
    duration_ms: args.durationMs,
    timestamp: new Date().toISOString(),
  };
}

export function emitSimulationRunCompleteAnalytics(args: {
  runId: string;
  ridesCount: number;
  fleetSize: number;
  successRatio?: number;
  durationMs: number;
}) {
  trackAnalyticsEvent(
    ANALYTICS_EVENT_NAMES.simulation_run_complete,
    buildSimulationRunCompletePayload(args)
  );
}

export function buildSimulationRunFailPayload(args: {
  errorCategory: string;
  errorCode: string;
  ridesCount: number;
  fleetSize: number;
  durationMs: number;
}) {
  return {
    feature_area: 'simulation' as const,
    error_category: args.errorCategory,
    error_code: args.errorCode,
    retryable: true,
    rides_count: args.ridesCount,
    fleet_size: args.fleetSize,
    duration_ms: args.durationMs,
    timestamp: new Date().toISOString(),
  };
}

export function emitSimulationRunFailAnalytics(args: {
  errorCategory: string;
  errorCode: string;
  ridesCount: number;
  fleetSize: number;
  durationMs: number;
}) {
  trackAnalyticsEvent(
    ANALYTICS_EVENT_NAMES.simulation_run_fail,
    buildSimulationRunFailPayload(args)
  );
}
